import { get, post } from '@/lib/api';
import { getAuthToken, setAuthSession } from '@/lib/auth';
import { AlertCircle, Code2, Loader2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthResponse {
  user: { id: string; email: string };
  token?: string;
  accessToken?: string;
  step?: string;
  mfaRequired?: boolean;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // On mount, check if the user is already authenticated via shared httpOnly cookie
  useEffect(() => {
    // If we already have a localStorage token, redirect immediately
    if (getAuthToken()) {
      navigate('/', { replace: true });
      return;
    }
    // Otherwise probe the API — shared httpOnly cookie will be sent via credentials: 'include'
    get<{ id: string; email: string; accessToken?: string }>('/auth/me')
      .then((user) => {
        // Shared session exists — store locally and redirect
        if (user.accessToken) {
          setAuthSession(user.accessToken, user);
        }
        navigate('/', { replace: true });
      })
      .catch(() => {
        // No shared session — show login form
        setCheckingSession(false);
      });
  }, [navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/signup';
      const result = await post<AuthResponse>(endpoint, { email, password });

      if (result.mfaRequired) {
        setError('MFA required — not yet supported in IDE');
        setLoading(false);
        return;
      }

      setAuthSession(result.accessToken || result.token || '', result.user);

      navigate('/');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1e1e1e] flex items-center justify-center">
      {checkingSession ? (
        <Loader2 className="w-8 h-8 text-[#858585] animate-spin" />
      ) : (
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <Code2 className="w-8 h-8 text-[#0e639c]" />
            <h1 className="text-2xl font-semibold text-white">Kubernodes IDE</h1>
          </div>

          {/* Card */}
          <div className="bg-[#252526] border border-[#3c3c3c] rounded-lg p-6">
            <h2 className="text-lg font-medium text-[#cccccc] mb-6 text-center">
              {mode === 'login' ? 'Sign in to continue' : 'Create your account'}
            </h2>

            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded bg-red-900/30 border border-red-800/50 text-red-300 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#858585] mb-1.5 uppercase tracking-wider">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-3 py-2 bg-[#3c3c3c] border border-[#555] rounded text-white text-sm placeholder-[#858585] focus:border-[#0e639c] focus:outline-none transition-colors"
                  placeholder="dev@example.com"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#858585] mb-1.5 uppercase tracking-wider">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-3 py-2 bg-[#3c3c3c] border border-[#555] rounded text-white text-sm placeholder-[#858585] focus:border-[#0e639c] focus:outline-none transition-colors"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-[#0e639c] hover:bg-[#1177bb] disabled:opacity-50 text-white rounded text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {mode === 'login' ? 'Signing in…' : 'Creating account…'}
                  </>
                ) : mode === 'login' ? (
                  'Sign In'
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  setMode(mode === 'login' ? 'signup' : 'login');
                  setError('');
                }}
                className="text-sm text-[#858585] hover:text-[#0e639c] transition-colors"
              >
                {mode === 'login'
                  ? "Don't have an account? Sign up"
                  : 'Already have an account? Sign in'}
              </button>
            </div>
          </div>

          <p className="text-center text-xs text-[#555] mt-6">
            AI-native smart contract development platform
          </p>
        </div>
      )}
    </div>
  );
}
